/**
 * resolver/types.js - Type definitions and constants for style resolver
 * Location: /app/frontend/src/admin/page_builder/resolver/
 * 
 * Defines the precedence hierarchy and output types for the style system.
 */

/**
 * Style source precedence (higher number = higher priority)
 * elementOverride > blockOverride > pageOverride > globalTokens > componentDefaults
 */
export const StyleSource = {
  COMPONENT_DEFAULT: 0,
  GLOBAL_TOKEN: 1,
  PAGE_OVERRIDE: 2,
  BLOCK_OVERRIDE: 3,
  ELEMENT_OVERRIDE: 4
};

/**
 * Human-readable labels for style sources
 */
export const StyleSourceLabel = {
  [StyleSource.COMPONENT_DEFAULT]: 'Default',
  [StyleSource.GLOBAL_TOKEN]: 'Global Token',
  [StyleSource.PAGE_OVERRIDE]: 'Page Override',
  [StyleSource.BLOCK_OVERRIDE]: 'Block Override',
  [StyleSource.ELEMENT_OVERRIDE]: 'Element Override'
};

/**
 * @typedef {Object} ResolvedStyleEntry
 * @property {string} value - The resolved CSS value
 * @property {StyleSource} source - Where this value came from
 * @property {boolean} isTokenRef - Whether the original value was a token reference
 * @property {string|null} tokenPath - The token path if isTokenRef is true
 */

/**
 * @typedef {Object} StyleResolverOutput
 * @property {Object<string, string>} cssStyles - Styles with var() for token refs
 * @property {Object<string, string>} resolvedStyles - Styles with resolved token values
 * @property {Object<string, ResolvedStyleEntry>} metadata - Detailed info per property
 */

/**
 * Create a resolved style entry
 * @param {string} value - The resolved value
 * @param {number} source - StyleSource enum value
 * @param {boolean} isTokenRef - Whether original was a token ref
 * @param {string|null} tokenPath - Token path if applicable
 * @returns {ResolvedStyleEntry}
 */
export function createStyleEntry(value, source, isTokenRef = false, tokenPath = null) {
  return {
    value,
    source,
    isTokenRef,
    tokenPath
  };
}

/**
 * Create empty resolver output
 * @returns {StyleResolverOutput}
 */
export function createEmptyOutput() {
  return {
    cssStyles: {},
    resolvedStyles: {},
    metadata: {}
  };
}

/**
 * Check if a value is null or undefined (for delta reset detection)
 * @param {*} value 
 * @returns {boolean}
 */
export function isResetValue(value) {
  return value === null || value === undefined;
}

export default {
  StyleSource,
  StyleSourceLabel,
  createStyleEntry,
  createEmptyOutput,
  isResetValue
};
